import type {
  MotionPropsFor,
  RadioButtonGroupProps,
  RadioButtonProps,
} from '@hover/blueprint';
import {
  Center,
  Flex,
  Motion,
  RadioButton,
  RadioButtonGroup,
  calc,
  cssVar,
  forwardRef,
  motion,
  splitLayoutProps,
  system,
  transition,
  useTheme,
} from '@hover/blueprint';
import type { ResponsiveValue } from '@hover/blueprint/chakra';
import { AnimatePresence } from 'framer-motion';
import React, { useMemo, useRef } from 'react';

import { hideScrollbars } from 'src/shared/helpers/styles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { nudge } from '../helpers';
import { useMaxWidthQuery, useScrollFade, useScrollTo } from '../hooks';

import type { ArrowButtonProps } from './ArrowButton';
import { ArrowButton } from './ArrowButton';
import { Skeleton } from './Skeleton';

const $overflowDistanceX = cssVar('selector-overflow-distance-x');
const $overflowDistanceY = cssVar('selector-overflow-distance-y');

export type SelectorItemProps = RadioButtonProps;

interface ArrowProps extends ArrowButtonProps {}

const Arrow = forwardRef<ArrowProps, 'button'>(({ sx, ...props }, ref) => {
  const { direction } = props;

  const positionProps = direction === 'start' ? { left: 0 } : { right: 0 };

  const multiplier = direction === 'start' ? -1 : 1;
  const x = multiplier * 10;

  return (
    <Center
      height="350"
      position="absolute"
      top="0"
      {...positionProps}
      sx={nudge(100, direction === 'start' ? 'left' : 'right')}
    >
      <ArrowButton
        _active={{ bg: 'darkAlpha.100' }}
        _hover={{ outlineColor: 'darkAlpha.400' }}
        animate={{ x: 0, opacity: 1 }}
        bg="neutral.0"
        border="width400"
        borderColor="darkAlpha.400"
        borderRadius="900"
        exit={{ x, opacity: 0 }}
        height="200"
        initial={{ x, opacity: 0 }}
        outline="1px solid transparent"
        outlineOffset="-2px"
        size="tiny"
        sx={{
          aspectRatio: '1',
          svg: {
            strokeLinecap: 'square',
            strokeWidth: '2.5',
            transform: `translateX(${1 * multiplier}px)`,
          },
          ...transition.easing250('outline', 'background'),
          ...sx,
        }}
        top="200"
        width="200"
        {...props}
        ref={ref}
      />
    </Center>
  );
});

export interface SelectorProps extends MotionPropsFor<RadioButtonGroupProps> {
  /**
   * Set the left arrow distance from left
   */
  arrowLeft?: number;
  /**
   * Set the right arrow distance from right
   */
  arrowRight?: number;
  isLoading?: boolean;
  /**
   * Number of skeleton placeholder items to display when in the loading state
   *
   * @default 10
   */
  placeholderCount?: number;
  /**
   * Horizontal overflow distance for scroll container.
   *
   * This is useful for cases where the `Selector` is in an element with
   * padding, but the `Selector` items need to be visible to the edge of the
   * page or a container. It also determines where the focus halo is rendered
   * when the scroll container is focused.
   */
  overflowDistanceX?: ResponsiveValue<string>;
  /**
   * Vertical overflow distance for scroll conatainer. This determines where the
   * focus halo is rendered when the scroll container is focused.
   */
  overflowDistanceY?: ResponsiveValue<string>;
}

const MotionRadioButtonGroupHideScroll = motion(
  hideScrollbars(RadioButtonGroup),
);

const Selector = forwardRef<SelectorProps, 'div'>(
  (
    {
      arrowLeft = 800,
      arrowRight = 800,
      children,
      isLoading = false,
      overflowDistanceX,
      overflowDistanceY,
      placeholderCount = 10,
      ...props
    },
    ref,
  ) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

    const belowTablet = useMaxWidthQuery('tablet');
    const { space } = useTheme();

    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollTo] = useScrollTo({ ref: scrollRef });

    const placeholders = useMemo(
      () => Array.from({ length: placeholderCount }).map((_, i) => i),
      [placeholderCount],
    );

    const {
      layoutProps,
      props: { borderRadius, rounded, background, bg, ...radioProps },
    } = splitLayoutProps(props);

    const {
      style: scrollStyle,
      isFadingStart,
      isFadingEnd,
    } = useScrollFade({
      ref: scrollRef,
      direction: 'horizontal',
      distance: '150px',
      threshold: '50px',
      padding: '0px',
    });

    return (
      <Flex
        background={background ?? bg}
        borderRadius={borderRadius ?? rounded}
        data-name="selector"
        height={{ base: 350, tablet: 400 }}
        paddingX={{ base: 500, tablet: 800 }}
        ref={ref}
        {...layoutProps}
      >
        <MotionRadioButtonGroupHideScroll
          borderRadius={{ base: 0, tablet: '300' }}
          display="flex"
          flexDirection="row"
          gap={200}
          marginX={calc.multiply(-1, $overflowDistanceX)}
          marginY={calc.multiply(-1, $overflowDistanceY)}
          overflowX={isLoading ? 'hidden' : 'scroll'}
          paddingX={$overflowDistanceX.reference}
          paddingY={$overflowDistanceY.reference}
          ref={scrollRef}
          style={scrollStyle}
          sx={{
            ...belowTablet({
              WebkitMaskImage: 'none !important',
              maskImage: 'none !important',
            }),
            [$overflowDistanceX.variable]: overflowDistanceX ?? space[200],
            [$overflowDistanceY.variable]: overflowDistanceY ?? space[200],
          }}
          variant="primary"
          {...radioProps}
        >
          {isLoading
            ? placeholders.map(i => (
                <Skeleton
                  flex={1}
                  isLoaded={!isLoading}
                  key={i}
                  rounded="round"
                >
                  <system.div height="100%" minWidth="600" />
                </Skeleton>
              ))
            : children}
        </MotionRadioButtonGroupHideScroll>
        <Motion.Firm>
          <AnimatePresence>
            {isFadingStart && !isLoading && (
              <Arrow
                aria-label={t('selector.scrollLeft')}
                direction="start"
                exit={{ x: 10, opacity: 0 }}
                initial={{ x: 10, opacity: 0 }}
                key="scroll-start"
                left={arrowLeft}
                onClick={() => scrollTo('left')}
              />
            )}
            {isFadingEnd && !isLoading && (
              <Arrow
                aria-label={t('selector.scrollRight')}
                direction="end"
                key="scroll-end"
                onClick={() => scrollTo('right')}
                right={arrowRight}
              />
            )}
          </AnimatePresence>
        </Motion.Firm>
      </Flex>
    );
  },
);

export { Selector, RadioButton as SelectorItem };
